import { useId, useRef, useState, type KeyboardEvent } from 'react';

import { filtrarOpciones, type Opcion } from './busqueda';

/** Cuántas se pintan a la vez. Las demás se alcanzan escribiendo. */
const VISIBLES = 60;

/**
 * Un desplegable que se busca escribiendo.
 *
 * Sustituye al `<select>` en las listas largas: con trescientos insumos, el
 * desplegable nativo obligaba a bajar con la rueda buscando «Pollo» entre las
 * pes, y en una tablet era peor. Aquí se escribe un trozo del nombre o del
 * código, en cualquier orden y sin tildes, y se elige con el dedo, con las
 * flechas o con Enter.
 *
 * Cerrado enseña lo elegido, como un desplegable. La lista se abre **debajo
 * del campo y dentro del formulario**, no flotando encima: los formularios
 * viven en ventanas con su propio desplazamiento, y una lista flotante quedaba
 * cortada por abajo justo en el último campo.
 */
export function Buscable({
  id,
  opciones,
  valor,
  onCambio,
  placeholder = 'Busca por nombre o código…',
  sinEleccion,
  deshabilitado = false,
  etiquetaAccesible,
}: {
  id?: string;
  opciones: readonly Opcion[];
  valor: string;
  onCambio: (valor: string) => void;
  placeholder?: string;
  /**
   * Cómo se lee no elegir nada —«Sin proveedor fijo», «Sin orden»—, si se
   * puede. Sin él, una vez elegido no hay forma de volver a vacío.
   */
  sinEleccion?: string;
  deshabilitado?: boolean;
  etiquetaAccesible?: string;
}) {
  const propio = useId();
  const idDelCampo = id ?? `buscable-${propio}`;
  const idDeLaLista = `${idDelCampo}-lista`;

  const [abierto, setAbierto] = useState(false);
  const [texto, setTexto] = useState('');
  const [activa, setActiva] = useState(0);
  const campo = useRef<HTMLInputElement>(null);

  const elegida = opciones.find((opcion) => opcion.valor === valor);
  const encontradas = filtrarOpciones(opciones, texto);
  const vacia: Opcion | null =
    sinEleccion === undefined || texto.trim() !== ''
      ? null
      : { valor: '', etiqueta: sinEleccion };
  const lista = [...(vacia === null ? [] : [vacia]), ...encontradas];
  const visibles = lista.slice(0, VISIBLES);

  const abrir = () => {
    if (deshabilitado) return;
    setTexto('');
    setActiva(Math.max(0, visibles.findIndex((opcion) => opcion.valor === valor)));
    setAbierto(true);
  };

  const elegir = (opcion: Opcion) => {
    onCambio(opcion.valor);
    setAbierto(false);
    setTexto('');
  };

  const teclas = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (!abierto) {
      if (evento.key === 'ArrowDown' || evento.key === 'Enter') {
        evento.preventDefault();
        abrir();
      }
      return;
    }

    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      setActiva((antes) => Math.min(antes + 1, visibles.length - 1));
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      setActiva((antes) => Math.max(antes - 1, 0));
    } else if (evento.key === 'Enter') {
      // Dentro de un formulario, Enter no lo envía mientras se está eligiendo.
      evento.preventDefault();
      const opcion = visibles[activa];
      if (opcion !== undefined) elegir(opcion);
    } else if (evento.key === 'Escape') {
      evento.preventDefault();
      setAbierto(false);
      setTexto('');
    }
  };

  const idDe = (indice: number) => `${idDelCampo}-opcion-${indice}`;

  return (
    <div className={`buscable${abierto ? ' buscable--abierto' : ''}`}>
      <input
        ref={campo}
        id={idDelCampo}
        type="text"
        role="combobox"
        autoComplete="off"
        aria-label={etiquetaAccesible}
        aria-expanded={abierto}
        aria-controls={idDeLaLista}
        aria-autocomplete="list"
        aria-activedescendant={abierto && visibles.length > 0 ? idDe(activa) : undefined}
        disabled={deshabilitado}
        placeholder={abierto ? placeholder : (sinEleccion ?? 'Elegir…')}
        value={abierto ? texto : (elegida?.etiqueta ?? '')}
        onFocus={abrir}
        onClick={() => {
          if (!abierto) abrir();
        }}
        onBlur={() => {
          setAbierto(false);
          setTexto('');
        }}
        onChange={(evento) => {
          setTexto(evento.target.value);
          setActiva(0);
          setAbierto(true);
        }}
        onKeyDown={teclas}
      />

      {abierto ? (
        <ul id={idDeLaLista} role="listbox" className="buscable__lista">
          {visibles.map((opcion, indice) => (
            <li
              key={opcion.valor === '' ? '__sin-eleccion' : opcion.valor}
              id={idDe(indice)}
              role="option"
              aria-selected={opcion.valor === valor}
              className={[
                'buscable__opcion',
                indice === activa ? 'buscable__opcion--activa' : '',
                opcion.valor === valor ? 'buscable__opcion--elegida' : '',
              ].join(' ')}
              // Antes del blur: con `onClick` el campo se cerraba primero y el
              // toque no llegaba a ninguna opción.
              onMouseDown={(evento) => {
                evento.preventDefault();
                elegir(opcion);
              }}
              onMouseEnter={() => setActiva(indice)}
            >
              <span className="buscable__nombre">{opcion.etiqueta}</span>
              {opcion.detalle === undefined ? null : (
                <span className="buscable__detalle">{opcion.detalle}</span>
              )}
            </li>
          ))}

          {lista.length === 0 ? (
            <li className="buscable__nota">Nada coincide con «{texto.trim()}».</li>
          ) : null}

          {lista.length > VISIBLES ? (
            <li className="buscable__nota">
              Y {lista.length - VISIBLES} más: sigue escribiendo para encontrarlos.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

/**
 * El cuadro de búsqueda de una tabla: filtra lo que ya está en pantalla.
 */
export function CuadroDeBusqueda({
  valor,
  onCambio,
  placeholder = 'Buscar…',
  etiquetaAccesible = 'Buscar en la lista',
}: {
  valor: string;
  onCambio: (valor: string) => void;
  placeholder?: string;
  etiquetaAccesible?: string;
}) {
  return (
    <input
      className="buscador"
      type="search"
      aria-label={etiquetaAccesible}
      placeholder={placeholder}
      value={valor}
      onChange={(evento) => onCambio(evento.target.value)}
    />
  );
}
